import { useState, type FormEvent, type ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function RegisterPage() {
  const { register } = useAuth();
  const { t } = useTranslation("auth");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // INVITATION-ONLY MODE: Block signup attempts
    setError(t("registerInvitationOnly"));
    return;

    // Original implementation kept for when signups are re-enabled
    /* eslint-disable-next-line no-unreachable */
    setIsLoading(true);
    setError("");

    if (formData.password !== formData.confirmPassword) {
      setError(t("passwordMismatch"));
      setIsLoading(false);
      return;
    }

    try {
      await register({
        email: formData.email,
        password: formData.password,
        givenName: formData.firstName,
        familyName: formData.lastName,
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || t("registerError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
 <div className="min-h-screen flex items-center justify-center bg-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
 <div className="max-w-md w-full space-y-8">
        <div>
 <div className="flex justify-center">
 <UserPlus className="h-12 w-12 text-primary-600" />
          </div>
 <h2 className="mt-6 text-center text-3xl font-extrabold text-secondary-900">
            {t("createYourAccount")}
          </h2>
 <p className="mt-2 text-center text-sm text-secondary-600">
            {t("or")}{" "}
            <Link
              to="/login"
 className="font-medium text-primary-600 hover:text-primary-500"
            >
              {t("signInToExistingAccount")}
            </Link>
          </p>
        </div>

 <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
 <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

 <div className="space-y-4">
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="firstName"
 className="block text-sm font-medium text-secondary-700"
                >
                  {t("givenName")}
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
 className="input mt-1"
                  placeholder={t("givenName")}
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label
                  htmlFor="lastName"
 className="block text-sm font-medium text-secondary-700"
                >
                  {t("familyName")}
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
 className="input mt-1"
                  placeholder={t("familyName")}
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
 className="block text-sm font-medium text-secondary-700"
              >
                {t("email")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
 className="input mt-1"
                placeholder={t("email")}
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <label
                htmlFor="password"
 className="block text-sm font-medium text-secondary-700"
              >
                {t("password")}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
 className="input mt-1"
                placeholder={t("password")}
                value={formData.password}
                onChange={handleChange}
              />
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
 className="block text-sm font-medium text-secondary-700"
              >
                {t("confirmPassword")}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
 className="input mt-1"
                placeholder={t("confirmPassword")}
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
 className="btn-primary w-full py-2 px-4 disabled:opacity-50"
            >
              {isLoading ? (
 <div className="flex items-center justify-center">
 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t("creatingAccount")}
                </div>
              ) : (
                t("createAccount")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
